import React, { useState } from 'react';
import { Link, usePage } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { useOrderDrawer, OrderDetailDrawer } from '../../components/order/OrderDetailDrawer';
import { DetailCard } from '../../components/DetailCard';
import { QuickViewButton } from '../../components/order/QuickViewButton';
import { OrderColumnSettings } from '../../components/order/OrderColumnSettings';
import { resolveColumns } from '../../components/order/orderColumns';
import { useTrans } from '../../hooks/useTrans';
import { formatDateShort, formatMoney } from '../../lib/formatters';

export default function HomeIndex({ remoteOrders, remoteOrdersError, visibleColumns, invoices, pageTranslations, pageRoutes }) {
    const { auth } = usePage().props;
    const translate = useTrans();
    const { needsValidation, permissions } = auth;
    const hasPermissions = Object.values(permissions).some(Boolean);
    const drawer = useOrderDrawer();
    const [columnKeys, setColumnKeys] = useState(() => resolveColumns(visibleColumns).map((column) => column.key));

    return (
        <div className="px-4 space-y-6">
            {needsValidation && (
                <Alert>
                    <h5 className="text-lg font-bold">{pageTranslations.needsValidation.title}</h5>
                    <p><strong>{pageTranslations.needsValidation.message[0]}</strong></p>
                    <p>{pageTranslations.needsValidation.message[1]} {pageTranslations.needsValidation.message[2]}</p>
                    <p>{pageTranslations.needsValidation.message[3]}</p>
                </Alert>
            )}

            {!needsValidation && !hasPermissions && (
                <Alert>
                    <h5 className="text-lg font-bold">{pageTranslations.noPermissions.title}</h5>
                    <p>{pageTranslations.noPermissions.message[0]}<br />{pageTranslations.noPermissions.message[1]}</p>
                </Alert>
            )}

            {(permissions.ordersCreate || permissions.ordersImport) && (
                <Panel title={translate('Snel naar')}>
                    <div className="flex flex-wrap gap-2">
                        {permissions.ordersCreate && (
                            <Button variant="primary" asChild><a href={pageRoutes.orderCreate}>{translate('Order invoeren')}</a></Button>
                        )}
                        {permissions.ordersImport && (
                            <Button variant="primary" asChild><a href={pageRoutes.orderImportCreate}>{translate('Orderbestand inlezen')}</a></Button>
                        )}
                    </div>
                </Panel>
            )}

            {permissions.ordersView && (
                <Panel
                    title={translate('Orders')}
                    actions={<OrderColumnSettings visibleColumns={columnKeys} onChange={setColumnKeys} />}
                    footer={remoteOrders && remoteOrders.total > 0 && (
                        <Button variant="clear" asChild className="pr-[38px]"><a href={pageRoutes.orderIndex}>{translate('Toon alle orders')} &rsaquo;</a></Button>
                    )}
                >
                    <OrdersTable orders={remoteOrders} error={remoteOrdersError} pageTranslations={pageTranslations} onOrderClick={drawer.openDrawer} columns={resolveColumns(columnKeys)} />
                </Panel>
            )}

            <OrderDetailDrawer {...drawer} />

            {permissions.invoicesView && (
                <Panel
                    title={translate('Laatste facturen')}
                    footer={invoices && invoices.length > 0 && (
                        <Button variant="clear" asChild className="pr-[38px]"><Link href={pageRoutes.invoiceIndex}>{translate('Toon alle facturen')} &rsaquo;</Link></Button>
                    )}
                >
                    <InvoicesTable invoices={invoices} />
                </Panel>
            )}
        </div>
    );
}

function Alert({ children }) {
    return (
        <div className="rounded-[3px] border border-[#bbb] bg-white">
            <div className="border-l-[3px] border-[#00857c] p-4 sm:p-[30px] text-[#003545] space-y-2">
                {children}
            </div>
        </div>
    );
}

function Panel({ title, actions, footer, children }) {
    return (
        <div className="rounded-[3px] border border-[#bbb] bg-white">
            <div className="p-4 sm:p-[30px]">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <h3 className="text-2xl font-bold text-[#003545]">{title}</h3>
                    {actions}
                </div>
                {children}
            </div>
            {footer && (
                <div className="border-t border-[#bbb] px-4 sm:px-[30px] py-4 text-right">
                    {footer}
                </div>
            )}
        </div>
    );
}

function OrdersTable({ orders, error, pageTranslations, onOrderClick, columns }) {
    const translate = useTrans();
    const orderList = orders && orders.data ? orders.data : [];
    const hasAutoOrders = orderList.some(order => order.source !== 'webshop');
    const renderContext = { translate, pageTranslations };
    const emptyMessage = error || translate('U heeft nog geen orders geplaatst.');

    return (
        <>
            <div className="hidden md:block overflow-x-auto">
                <Table>
                    <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                        <TableRow className="hover:bg-[#ededed]">
                            <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm w-0"></TableHead>
                            {columns.map((column) => (
                                <TableHead key={column.key} className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate(column.label)}</TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {orderList.length > 0 ? (
                            orderList.map((order, index) => (
                                <TableRow key={order.internal_id || index} className={index % 2 === 1 ? 'bg-[#f5f5f5]' : ''}>
                                    <TableCell className="py-5 px-2.5">
                                        {order.internal_id && (
                                            <QuickViewButton onClick={() => onOrderClick(order.internal_id)} title={translate('Snelweergave')} />
                                        )}
                                    </TableCell>
                                    {columns.map((column) => (
                                        <TableCell key={column.key} className={`py-5 px-2.5 ${column.className || ''}`}>
                                            {column.render(order, renderContext)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1} className="py-5 px-2.5 text-center text-gray-500">
                                    {emptyMessage}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="md:hidden">
                {orderList.length > 0 ? (
                    <ul className="border-y border-[#d4d4d4]">
                        {orderList.map((order, index) => (
                            <DetailCard
                                key={order.internal_id || index}
                                item={order}
                                fields={columns}
                                context={renderContext}
                                striped={index % 2 === 1}
                                action={order.internal_id && <QuickViewButton onClick={() => onOrderClick(order.internal_id)} title={translate('Snelweergave')} />}
                            />
                        ))}
                    </ul>
                ) : (
                    <p className="py-5 px-2.5 text-center text-gray-500">{emptyMessage}</p>
                )}
            </div>
            {hasAutoOrders && (
                <p className="mt-2 text-sm italic text-gray-500">* {translate('Betreft een automatisch ingediende order.')}</p>
            )}
        </>
    );
}

const INVOICE_FIELDS = [
    { key: 'date', label: 'Datum', className: 'whitespace-nowrap', render: (invoice) => formatDateShort(invoice.date) },
    { key: 'number', label: 'Factuurnummer', render: (invoice) => invoice.number },
    { key: 'debtor', label: 'Debiteur', render: (invoice) => invoice.debtor?.company_name },
    { key: 'amount', label: 'Bedrag', className: 'whitespace-nowrap', render: (invoice) => <>&euro; {formatMoney(invoice.amount)}</> },
    {
        key: 'download',
        label: 'Downloaden',
        render: (invoice, { translate }) => (
            <a href={`/facturen/${invoice.number}/downloaden`} className="inline-flex items-center h-[44px] rounded-[6px] border-2 border-[#ededed] px-6 text-[14px] font-black text-[#003545] hover:bg-[#ededed] transition-colors">
                {translate('Download PDF')}
            </a>
        ),
    },
];

function InvoicesTable({ invoices }) {
    const translate = useTrans();
    const renderContext = { translate };
    const hasInvoices = invoices && invoices.length > 0;
    const emptyMessage = translate('Er zijn geen facturen gevonden.');

    return (
        <>
            <div className="hidden md:block overflow-x-auto">
                <Table>
                    <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                        <TableRow className="hover:bg-[#ededed]">
                            {INVOICE_FIELDS.map((field) => (
                                <TableHead key={field.key} className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate(field.label)}</TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {hasInvoices ? (
                            invoices.map((invoice, index) => (
                                <TableRow key={invoice.number} className={index % 2 === 1 ? 'bg-[#f5f5f5]' : ''}>
                                    {INVOICE_FIELDS.map((field) => (
                                        <TableCell key={field.key} className={`py-5 px-2.5 ${field.className || ''}`}>
                                            {field.render(invoice, renderContext)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={INVOICE_FIELDS.length} className="py-5 px-2.5 text-center text-gray-500">
                                    {emptyMessage}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="md:hidden">
                {hasInvoices ? (
                    <ul className="border-y border-[#d4d4d4]">
                        {invoices.map((invoice, index) => (
                            <DetailCard key={invoice.number} item={invoice} fields={INVOICE_FIELDS} context={renderContext} striped={index % 2 === 1} />
                        ))}
                    </ul>
                ) : (
                    <p className="py-5 px-2.5 text-center text-gray-500">{emptyMessage}</p>
                )}
            </div>
        </>
    );
}
